import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Doctor, TimeSlot } from '@/types/clinic';
import { useClinicConfigContext } from '@/contexts/ClinicConfigContext';
import { useClinicProviders } from '@/hooks/useClinicData';
import { SelectionButton } from '@/components/PatientTypeButton';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Footer } from '@/components/Footer';

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.8 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

const cardVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: 'easeOut' },
  },
};

export function ProviderSelectPage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const navigate = useNavigate();
  const { clinic, clinicLoading, clinicError } = useClinicConfigContext();
  const [selectedProvider, setSelectedProvider] = useState<Doctor | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);

  // Get service ID from cookie
  const getServiceId = () => {
    const cookieMatch = document.cookie
      .split('; ')
      .find(row => row.startsWith('selected_service_id='));
    return cookieMatch ? decodeURIComponent(cookieMatch.split('=')[1]) : null;
  };

  const serviceId = getServiceId();

  // Use React Query for providers with caching
  const {
    data: providersResponse,
    isLoading: providersLoading,
    error: providersError,
  } = useClinicProviders(clinicSlug || '', serviceId || '');

  const providers = providersResponse?.success
    ? (providersResponse.providers || []).map(p => ({
        ...p,
        availableSlots: (p.availableSlots || []) as TimeSlot[],
      }))
    : [];

  // Theme is initialized globally in App via ThemeInitializer

  if (clinicLoading || providersLoading) {
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  if (clinicError || providersError || !clinic) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>404</h1>
          <p className='text-xl text-gray-600 mb-8'>
            {providersError?.message || 'Clinic not found'}
          </p>
        </div>
      </div>
    );
  }

  if (!serviceId) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>Error</h1>
          <p className='text-xl text-gray-600 mb-8'>No service selected</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className='h-screen w-screen overflow-hidden relative flex flex-col'
      variants={pageVariants}
      initial='initial'
      animate='animate'
      exit='exit'
    >
      {/* Header with Logo */}
      <motion.div className='flex-shrink-0 p-6' variants={fadeInUp}>
        <div className='flex items-center'>
          <div className='w-fit'>
            <img
              src={'/logo.png'}
              alt={`${clinic.name} logo`}
              className='w-full h-full object-contain'
            />
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className='flex-1 flex flex-col justify-center px-6 py-8'>
        <div className='max-w-4xl mx-auto w-full'>
          {/* Welcome Message */}
          <motion.div
            className='text-left mb-12 mt-8 lg:px-20'
            variants={fadeInUp}
          >
            <h2
              className='text-xl lg:text-5xl font-semibold  mb-6'
              style={{ color: 'var(--clinic-text)' }}
            >
              I'm now analyzing the schedule for openings. Please select an
              available dentist below.
            </h2>
          </motion.div>

          {/* Provider Selection */}
          <motion.div
            className='grid md:grid-cols-2 lg:grid-cols-2 gap-6 mb-8 lg:px-20'
            variants={staggerContainer}
            initial='initial'
            animate='animate'
          >
            {providers.map(doctor => {
              const isSelected = selectedProvider?.id === doctor.id;
              const getInitials = (fullName: string): string => {
                if (!fullName) return '';
                const parts = fullName.trim().split(/\s+/);
                const first = parts[0]?.[0] || '';
                const last =
                  parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
                return (first + last).toUpperCase();
              };
              const initials = getInitials(doctor.name);

              return (
                <motion.div key={doctor.id} variants={cardVariants}>
                  <SelectionButton
                    isSelected={isSelected}
                    onClick={() => setSelectedProvider(doctor)}
                    className='w-full text-left'
                  >
                    <div>
                      <div className='flex flex-col h-full'>
                        {/* Top Section with Avatar and Info */}
                        <div className='flex items-start space-x-4 flex-1'>
                          {/* Doctor Avatar */}
                          <div className='flex-shrink-0'>
                            <div
                              className='w-16 h-16 overflow-hidden rounded-full border-2'
                              style={{
                                borderColor: isSelected
                                  ? 'var(--clinic-primary)'
                                  : '#14B8A6',
                              }}
                            >
                              {doctor.image ? (
                                <img
                                  src={doctor.image}
                                  alt={doctor.name}
                                  className='w-full h-full object-cover'
                                />
                              ) : (
                                <div className='w-full h-full flex items-center justify-center bg-white'>
                                  <span className='text-lg font-semibold text-gray-900'>
                                    {initials}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Doctor Info */}
                          <div className='flex-1 min-w-0'>
                            {doctor.specialization && (
                              <p
                                className={`font-medium text-base mb-1 ${isSelected ? 'text-white/90' : 'text-gray-500'}`}
                              >
                                {doctor.specialization}
                              </p>
                            )}
                            <h4
                              className={`font-medium text-lg `}
                              style={{ color: 'var(--clinic-text)' }}
                            >
                              {doctor.name}
                            </h4>
                          </div>
                        </div>

                        {/* Next Available Appointment - Full Width at Bottom */}
                        <div
                          className='flex items-center justify-between mt-4 pt-4 '
                          style={{ color: 'var(--clinic-text)' }}
                        >
                          <span className='text-sm font-medium '>
                            Next available appointment:
                          </span>
                          <span className='text-sm font-medium '>
                            {doctor.availableSlots &&
                            doctor.availableSlots.length > 0
                              ? new Date(
                                  doctor.availableSlots[0].date
                                ).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                })
                              : 'Sept 28th'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </SelectionButton>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Next Button */}
          <motion.div className='flex justify-center' variants={scaleIn}>
            <motion.button
              disabled={!selectedProvider || isNavigating}
              onClick={async () => {
                if (selectedProvider && !isNavigating) {
                  setIsNavigating(true);
                  try {
                    document.cookie = `selected_provider_id=${encodeURIComponent(
                      selectedProvider.id
                    )}; path=/; max-age=604800`;
                    // Navigate to date selection page with selected provider
                    navigate(`/${clinicSlug}/date-select`, {
                      state: { selectedProvider },
                    });
                  } catch (error) {
                    console.error('Navigation error:', error);
                    setIsNavigating(false);
                  }
                }
              }}
              className={`h-[54px] min-w-[300px] px-6 py-3 rounded-xl text-base transition-all duration-300 flex items-center justify-center gap-2.5  font-semibold hover:opacity-90 ${
                !selectedProvider || isNavigating
                  ? 'opacity-80 cursor-not-allowed'
                  : ''
              }`}
              style={{
                backgroundColor: 'var(--clinic-secondary)',
                color: 'var(--clinic-text)',
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isNavigating ? (
                <>
                  <LoadingSpinner size='sm' text='' />
                  Loading...
                </>
              ) : (
                'Next'
              )}
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <motion.div variants={fadeInUp}>
        <Footer useMotion={false} />
      </motion.div>
    </motion.div>
  );
}
